const MES_COMPLETO_A_CORTO: Record<string, string> = {
  enero: 'Ene',
  febrero: 'Feb',
  marzo: 'Mar',
  abril: 'Abr',
  mayo: 'May',
  junio: 'Jun',
  julio: 'Jul',
  agosto: 'Ago',
  septiembre: 'Sep',
  octubre: 'Oct',
  noviembre: 'Nov',
  diciembre: 'Dic',
  ene: 'Ene',
  feb: 'Feb',
  mar: 'Mar',
  abr: 'Abr',
  may: 'May',
  jun: 'Jun',
  jul: 'Jul',
  ago: 'Ago',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dic: 'Dic',
}

const MES_NUM_A_CORTO = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
]

const MES_COMPLETO_A_NUM: Record<string, string> = {
  enero: '01', febrero: '02', marzo: '03', abril: '04',
  mayo: '05', junio: '06', julio: '07', agosto: '08',
  septiembre: '09', octubre: '10', noviembre: '11', diciembre: '12',
  ene: '01', feb: '02', mar: '03', abr: '04',
  may: '05', jun: '06', jul: '07', ago: '08',
  sep: '09', oct: '10', nov: '11', dic: '12',
}

const MES_NUM_A_LARGO = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/**
 * Normaliza formatos heterogéneos del campo `periodo` a "Mmm YYYY".
 *
 * El calendario fiscal mezcla múltiples formatos desde distintas fuentes:
 *   - "03/2026"          (MM/YYYY, vencimientos_fiscales generados)
 *   - "Marzo/2026"       (mes completo, calendario AFIP)
 *   - "Mar/26"           (mes abreviado, calendario AFIP)
 *   - "1º quincena Abril/2026"    (formatos especiales)
 *   - "2º anticipo del año 2026"
 *   - "año 2025"
 *   - "ejercicios con cierre en Abril/2026"
 *
 * Formatos mensuales se normalizan a "Mmm YYYY".
 * Formatos especiales se devuelven tal cual (preservan semántica).
 */
export function formatPeriodo(periodo: string | null | undefined): string {
  if (!periodo) return '—'
  const raw = periodo.trim()
  if (!raw) return '—'

  // "03/2026" → "Mar 2026"
  const mmYYYY = raw.match(/^(\d{2})\/(\d{4})$/)
  if (mmYYYY) {
    const mesIdx = parseInt(mmYYYY[1], 10) - 1
    if (mesIdx >= 0 && mesIdx < 12) {
      return `${MES_NUM_A_CORTO[mesIdx]} ${mmYYYY[2]}`
    }
  }

  // "Marzo/2026" o "Mar/2026" → "Mar 2026"
  const mesTextYYYY = raw.match(/^([A-Za-záéíóú]+)\/(\d{4})$/)
  if (mesTextYYYY) {
    const corto = MES_COMPLETO_A_CORTO[mesTextYYYY[1].toLowerCase()]
    if (corto) return `${corto} ${mesTextYYYY[2]}`
  }

  // "Mar/26" → "Mar 2026"
  const mesCortoYY = raw.match(/^([A-Za-z]{3})\/(\d{2})$/)
  if (mesCortoYY) {
    const lower = mesCortoYY[1].toLowerCase()
    if (MES_COMPLETO_A_NUM[lower]) {
      const capitalizado = lower.charAt(0).toUpperCase() + lower.slice(1)
      return `${capitalizado} 20${mesCortoYY[2]}`
    }
  }

  return raw
}

/**
 * Convierte un período fiscal a formato largo en español ("abril de 2026").
 *
 * Usado en templates de email donde el formato compacto "Mar 2026" no es natural.
 * Reutiliza los mismos mapeos heterogéneos que `formatPeriodo`.
 *
 * - "04/2026" → "abril de 2026"
 * - "Abril/2026" → "abril de 2026"
 * - "Abr/26" → "abril de 2026"
 * - "año 2025" → "año 2025" (passthrough — preserva semántica de formatos especiales)
 * - null/"" → "" (defensivo, sin "—" porque va dentro de oraciones)
 */
export function formatPeriodoLargo(periodo: string | null | undefined): string {
  if (!periodo) return ''
  const raw = periodo.trim()
  if (!raw) return ''

  // "04/2026" → "abril de 2026"
  const mmYYYY = raw.match(/^(\d{2})\/(\d{4})$/)
  if (mmYYYY) {
    const mesIdx = parseInt(mmYYYY[1], 10) - 1
    if (mesIdx >= 0 && mesIdx < 12) {
      return `${MES_NUM_A_LARGO[mesIdx]} de ${mmYYYY[2]}`
    }
  }

  // "Marzo/2026" o "Mar/2026" → "marzo de 2026"
  const mesTextYYYY = raw.match(/^([A-Za-záéíóú]+)\/(\d{4})$/)
  if (mesTextYYYY) {
    const mesNum = MES_COMPLETO_A_NUM[mesTextYYYY[1].toLowerCase()]
    if (mesNum) {
      return `${MES_NUM_A_LARGO[parseInt(mesNum, 10) - 1]} de ${mesTextYYYY[2]}`
    }
  }

  // "Mar/26" → "marzo de 2026"
  const mesCortoYY = raw.match(/^([A-Za-z]{3})\/(\d{2})$/)
  if (mesCortoYY) {
    const mesNum = MES_COMPLETO_A_NUM[mesCortoYY[1].toLowerCase()]
    if (mesNum) {
      return `${MES_NUM_A_LARGO[parseInt(mesNum, 10) - 1]} de 20${mesCortoYY[2]}`
    }
  }

  return raw
}

/**
 * Devuelve una clave "YYYY-MM" para sort/filter en tablas.
 * Para formatos no-mensuales devuelve "9999-" + raw (quedan al final
 * ordenados alfabéticamente entre sí).
 */
export function periodoSortKey(periodo: string | null | undefined): string {
  if (!periodo) return '9999-99'
  const raw = periodo.trim()
  if (!raw) return '9999-99'

  // "03/2026" → "2026-03"
  const mmYYYY = raw.match(/^(\d{2})\/(\d{4})$/)
  if (mmYYYY) {
    return `${mmYYYY[2]}-${mmYYYY[1]}`
  }

  // "Marzo/2026" o "Mar/2026" → "2026-03"
  const mesTextYYYY = raw.match(/^([A-Za-záéíóú]+)\/(\d{4})$/)
  if (mesTextYYYY) {
    const mesNum = MES_COMPLETO_A_NUM[mesTextYYYY[1].toLowerCase()]
    if (mesNum) return `${mesTextYYYY[2]}-${mesNum}`
  }

  // "Mar/26" → "2026-03"
  const mesCortoYY = raw.match(/^([A-Za-z]{3})\/(\d{2})$/)
  if (mesCortoYY) {
    const mesNum = MES_COMPLETO_A_NUM[mesCortoYY[1].toLowerCase()]
    if (mesNum) return `20${mesCortoYY[2]}-${mesNum}`
  }

  return `9999-${raw}`
}
